import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import CommModalContainer from './CommModalContainer';
import { omit } from 'ui-addon-upgrades/utils';
import { correctUpgradeData, getIsFreeUser, overrideAssignable, trackContactSalesButtonClick, trackContactSalesButtonView, trackCommModalClose } from 'ui-addon-upgrades/_core/contactSales/utils';
import { getIsRetailPortal } from 'ui-addon-upgrades/_core/common/api/fetchRetail';
import { getPropertyRepInfo } from 'ui-addon-upgrades/_core/common/data/upgradeData/transformers/getPropertyRepInfo';
import { prefetchMeetingData } from '../common/api/meetingHelpers';
import { ContactSalesButtonPropsInterface } from 'ui-addon-upgrades/_core/contactSales/ContactSalesPropsInterface';
import { sourceKeys } from 'ui-addon-upgrades/_core/common/data/upgradeData/properties/sources';
import { sequence } from 'ui-addon-upgrades/_core/contactSales/utils';
import { debounce } from 'ui-addon-upgrades/utils';
const MODAL_STATE = {
  CLOSED: 'CLOSED',
  LOADING: 'LOADING',
  OPEN: 'OPEN'
};

const correctUpgradeDataHOC = Component => props => {
  return /*#__PURE__*/_jsx(Component, Object.assign({}, props, {
    upgradeData: correctUpgradeData(props.upgradeData),
    isAssignable: overrideAssignable(props.isAssignable)
  }));
};

let repInfoPromise;
let isFreeUserPromise;
let isRetailPortalPromise;

const ContactSalesButton = props => {
  const {
    isAssignable = true,
    onClick = () => {},
    onCancel = () => {}
  } = props;
  const [modalState, setModalState] = useState(MODAL_STATE.CLOSED);
  const isFreeUser = useRef();
  const isRetailPortal = useRef();
  const repInfo = useRef();
  useEffect(() => {
    trackContactSalesButtonView(props);
    repInfoPromise = getPropertyRepInfo(props.upgradeData, sourceKeys.CONTACT_SALES_BUTTON, props);
    isFreeUserPromise = getIsFreeUser();
    isRetailPortalPromise = getIsRetailPortal();
    prefetchMeetingData();
  }, [props]);

  const handleClick = () => {
    setModalState(MODAL_STATE.LOADING);
    Promise.all([repInfoPromise, isFreeUserPromise, isRetailPortalPromise]).then(([repInfoResponse, isFreeUserResponse, isRetailPortalResponse]) => {
      repInfo.current = repInfoResponse.repInfo;
      isFreeUser.current = isFreeUserResponse;
      isRetailPortal.current = isRetailPortalResponse;
    }).catch(() => {
      repInfo.current = {};
      isFreeUser.current = true;
      isRetailPortal.current = false;
      Raven.captureMessage('Communication methods modal opened without setting data');
    }).finally(() => {
      trackContactSalesButtonClick(props, isRetailPortal.current, repInfo.current);
      setModalState(MODAL_STATE.OPEN);
    });
  };

  const handleClose = () => {
    trackCommModalClose(props.upgradeData, repInfo.current);
    setModalState(MODAL_STATE.CLOSED);
    onCancel();
  };

  const Button = () => {
    const CLICK_DELAY = 50;
    const debouncedHandleClick = debounce(handleClick, CLICK_DELAY);
    const onClickSequence = sequence(debouncedHandleClick, onClick);
    const propsToPass = omit(props, ['usageLimitBannerData', 'forwardedRef', 'onClick', 'upgradeData', 'allowModal', 'isAssignable', 'termId', 'additionalUnitQuantity', 'isUpgradePoint', '_textOverride', '_overrideCommunicationMethodKey', '_buttonOverride', '_overrideModalHeaderText', 'modalRoot']);

    if (props._buttonOverride) {
      return /*#__PURE__*/_jsx("div", Object.assign({
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-reagan-primary-cta": true
      }, propsToPass, {
        children: props._buttonOverride
      }));
    } else {
      return /*#__PURE__*/_jsx(UILoadingButton, Object.assign({
        loading: modalState === MODAL_STATE.LOADING,
        buttonRef: props.forwardedRef,
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-reagan-primary-cta": true
      }, propsToPass, {
        children: props._textOverride || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.contactSalesButton.text"
        })
      }));
    }
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Button, {}), modalState === MODAL_STATE.OPEN && /*#__PURE__*/_jsx(CommModalContainer, {
      handleClose: handleClose,
      isFreeUser: isFreeUser.current,
      isRetailPortal: isRetailPortal.current,
      isAssignable: isAssignable,
      modalRoot: props.modalRoot,
      upgradeData: props.upgradeData,
      repInfo: repInfo.current,
      _overrideCommunicationMethodKey: props._overrideCommunicationMethodKey,
      _overrideModalHeaderText: props._overrideModalHeaderText,
      isUpgradePoint: props.isUpgradePoint
    })]
  });
};

ContactSalesButton.defaultProps = {
  use: 'primary'
};
ContactSalesButton.propTypes = ContactSalesButtonPropsInterface;
export default correctUpgradeDataHOC(ContactSalesButton);